import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  ViewEncapsulation,
} from '@angular/core';
import {
  FaceItemModel,
  FilterSelection,
} from '@fantaz/shared/data-access/models';

@Component({
  selector: 'fz-filter-section',
  templateUrl: './filter-section.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('chipAnimation', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '250ms cubic-bezier(.4,0,.2,1)',
          style({ opacity: 1, transform: 'none' }),
        ),
      ]),
    ]),
  ],
  styleUrls: ['./filter-section.scss'],
})
export class FilterSection {
  limitIncrementStep = 6;

  /**
   * Section key identifier, this is used to identify the selected items in the
   * filters for the section.
   */
  key = input.required<string>();

  /**
   * Section title.
   */
  title = input<string>('');

  /**
   * Section items.
   */
  items = input<FaceItemModel[]>([]);

  /**
   * Section filters.
   */
  filters = input<FilterSelection>({});
  /**
   * Event emitter that emits the updated filter selection whenever an item is selected or unselected.
   * The emitted value is an object where the keys are filter keys and the values are arrays of selected item keys.
   */
  selection = output<FilterSelection>();

  /**
   * Number of items to display.
   */
  limit = signal<number>(this.limitIncrementStep);

  /**
   * Visible items in the section, the all items are limited by the limit signal.
   */
  visibleItems = computed<(FaceItemModel & { selected: boolean })[]>(() => {
    const selected = this.filters()[this.key()] || [];
    const showMore =
      this.items().length - this.limit() > this.limitIncrementStep;

    return this.items()
      .slice(0, showMore ? this.limit() : this.items().length)
      .map((item) => ({
        ...item,
        selected: selected.includes(item.key),
      }));
  });

  /**
   * Unselects an item from the filter selection.
   *
   * @param item The item to unselect.
   */
  unselectItem(item: FaceItemModel) {
    const key = this.key();
    const selected = this.filters()[key] || [];

    this.selection.emit({
      ...this.filters(),
      [key]: selected.filter((k) => k !== item.key),
    });
  }

  /**
   * Selects an item from the filter selection.
   *
   * @param item The item to select.
   */
  selectItem(item: FaceItemModel) {
    const key = this.key();
    const selected = this.filters()[key] || [];

    this.selection.emit({
      ...this.filters(),
      [key]: [...new Set([...selected, item.key])],
    });
  }

  /**
   * Toggles the selection of an item.
   *
   * @param item The item to toggle selection for.
   * @param selected Whether the item is currently selected.
   */
  toggleItemSelection(item: FaceItemModel, selected: boolean) {
    if (selected) {
      this.unselectItem(item);
    } else {
      this.selectItem(item);
    }
  }

  /**
   * Show more items.
   */
  showMore() {
    this.limit.update((prev) => prev + this.limitIncrementStep);
  }
}
